import { SkyButton, SkyMenu, type SkyMenuContent } from "@skypointcloud/skypoint-ui";
import type { RunEventExportFormat, RunSummaryRow } from "@shared/types/runs";
import { CancelIcon, DownloadIcon, MoreActionsIcon, RetryIcon } from "@/lib/icons";
import { connectionBusyReason } from "./retryTargets";
import { isTerminalRun } from "./runFormatting";

/**
 * The runs list's per-row menu (Session 10, B13).
 *
 * Never empty: both exports are reads every role that sees the list may make (decision Q2),
 * so a Viewer's menu holds exactly those three (the readable log, CSV and JSON Lines). The writes are absent, not disabled, without
 * their permission, and each appears only when it can apply: Retry on a finished run that did
 * not succeed (it opens the run page with the retry dialog, which says what it will re-run; off,
 * with the reason, while another run holds one of its connections, #1200),
 * Cancel on a run that is still moving.
 */

export interface RunRowMenuProps {
  row: RunSummaryRow;
  canRetry: boolean;
  canCancel: boolean;
  onExport: (row: RunSummaryRow, format: RunEventExportFormat) => void;
  onRetry: (row: RunSummaryRow) => void;
  onCancel: (row: RunSummaryRow) => void;
}

export function runRowMenuItems({
  row,
  canRetry,
  canCancel,
  onExport,
  onRetry,
  onCancel,
}: RunRowMenuProps): SkyMenuContent[] {
  const items: SkyMenuContent[] = [
    {
      key: "download-log",
      label: "Download log (.txt)",
      icon: <DownloadIcon className="size-4" />,
      onClick: () => onExport(row, "txt"),
    },
    {
      key: "export-csv",
      label: "Export events (CSV)",
      icon: <DownloadIcon className="size-4" />,
      onClick: () => onExport(row, "csv"),
    },
    {
      key: "export-jsonl",
      label: "Export events (JSON Lines)",
      icon: <DownloadIcon className="size-4" />,
      onClick: () => onExport(row, "jsonl"),
    },
  ];
  const terminal = isTerminalRun(row.status);
  // #1042: a cancelled run has no failed step to retry from (the run page refuses it too).
  if (canRetry && terminal && row.status !== "succeeded" && row.status !== "cancelled") {
    // #1200: while another run holds a connection of this run the server refuses the retry, so
    // the item is off and says why, the run page's own sentence.
    const busy = connectionBusyReason(row);
    items.push({
      key: "retry",
      label: "Retry from failed step",
      icon: <RetryIcon className="size-4" />,
      ...(busy ? { disabled: true, description: busy } : {}),
      onClick: () => {
        if (!busy) onRetry(row);
      },
    });
  }
  if (canCancel && !terminal) {
    items.push({
      key: "cancel",
      label: "Cancel run",
      icon: <CancelIcon className="size-4" />,
      danger: true,
      onClick: () => onCancel(row),
    });
  }
  return items;
}

export function RunRowMenu(props: RunRowMenuProps) {
  return (
    <SkyMenu
      align="end"
      items={runRowMenuItems(props)}
      trigger={
        <SkyButton
          appearance="subtle"
          size="small"
          icon={MoreActionsIcon}
          aria-label={`More actions for run ${props.row.runId}`}
          testId={`run-actions-${props.row.runId}`}
        />
      }
    />
  );
}
